import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { Check, ChevronRight, LockKeyhole, Waves } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import type { MonthlyCatalog, PaddleAccountStatus, PaddleCheckoutBootstrap } from "@shared/paddle-api";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import { openPaddleCheckout } from "@/lib/paddleCheckout";
import type { MonthlyPaddleCheckoutRequest } from "@shared/paddle-checkout";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const BILLING_KEY = ["/api/billing/subscription"] as const;
const features = {
  free: ["Live buoy conditions where available", "Five-day swell and wind forecasts", "Tide, sunrise and sunset", "Save spots around the world"],
  pro: ["Spot-specific swell, wind and tide alerts", "Scheduled daily surf reports", "Tomorrow’s Outlook and Forecast Match", "SMS, email or push delivery"],
  agent: ["Everything in Pro", "Surf-focused AI for real conditions", "Ask about forecasts and live conditions", "More room for alerts and conversations"],
};

function uuid() {
  return globalThis.crypto?.randomUUID?.() ?? "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === "x" ? r : (r & 3 | 8)).toString(16);
  });
}

export default function PricingPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<{ tone: "pending" | "error"; text: string } | null>(null);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const confirmOnce = useRef(false);
  const nativeIos = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
  const catalog = useQuery<MonthlyCatalog>({ queryKey: ["/api/billing/checkout-config"], staleTime: 60_000 });
  const account = useQuery<PaddleAccountStatus>({ queryKey: BILLING_KEY, enabled: isAuthenticated });

  const checkout = useMutation({
    mutationFn: async (tier: "pro" | "agent") => {
      const response = await apiRequest("/api/billing/checkout", {
        method: "POST", body: { tier, interval: "monthly", requestId: uuid() } satisfies MonthlyPaddleCheckoutRequest,
      });
      return response.json() as Promise<PaddleCheckoutBootstrap>;
    },
    onSuccess: async (bootstrap) => {
      confirmOnce.current = false;
      setCheckoutBusy(true);
      setNotice({ tone: "pending", text: "Checkout opened. We’ll verify your subscription with LiveSwell after Paddle reports the transaction." });
      try {
        await openPaddleCheckout(bootstrap, event => {
            if ((event.name === "checkout.completed" || event.name === "checkout.closed") && !confirmOnce.current) {
              confirmOnce.current = true;
              const transactionId = event.data?.transaction_id ?? bootstrap.transactionId;
              void apiRequest("/api/billing/checkout/confirm", { method: "POST", body: { transactionId } })
                .then(response => response.json() as Promise<PaddleAccountStatus>)
                .then(status => {
                  queryClient.setQueryData(BILLING_KEY, status);
                  if (status.providerState === "known" && status.tier !== "free") {
                    setNotice({ tone: "pending", text: "Your subscription is confirmed. Your account is ready." });
                  } else {
                    setNotice({ tone: "pending", text: "Payment is not confirmed yet. No access change has been assumed. Check again in Account shortly." });
                  }
                })
                .catch(() => {
                  setNotice({ tone: "error", text: "We couldn’t verify the transaction yet. No access change has been assumed; visit Account and retry reconciliation." });
                })
                .finally(() => setCheckoutBusy(false));
            }
        });
      } catch (error) {
        setCheckoutBusy(false);
        setNotice({ tone: "error", text: error instanceof Error ? error.message : "Checkout could not be opened. Please try again." });
      }
    },
    onError: error => setNotice({ tone: "error", text: error.message || "Checkout could not start. Please try again." }),
  });

  const start = (tier: "pro" | "agent") => {
    if (nativeIos) return;
    if (!isAuthenticated) {
      navigate(`/sign-in?redirect_url=${encodeURIComponent(`/pricing?tier=${tier}`)}`);
      return;
    }
    if (account.data?.tier !== "free" || account.data?.pendingOperation) {
      navigate("/account");
      return;
    }
    confirmOnce.current = false;
    checkout.mutate(tier);
  };

  const available = catalog.data?.available === true;
  const offers = catalog.data?.offers;
  const paidAccount = account.data && account.data.tier !== "free";
  const safeToBuy = !isAuthenticated || (
    !account.isLoading &&
    !account.isError &&
    account.data?.providerState === "known" &&
    account.data.tier === "free" &&
    !account.data.pendingOperation
  );

  return (
    <div className="ls-shell min-h-[100dvh] flex flex-col text-slate-100">
      <Header />
      <main className="w-full max-w-6xl mx-auto px-5 pb-16">
        <header className="mx-auto max-w-3xl text-center pt-12 md:pt-16 pb-9">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-300/20 bg-emerald-300/[.06] px-3 py-1.5 text-[10px] font-bold tracking-[.18em] text-emerald-200 uppercase">
            <Waves size={13} /> Simple monthly plans
          </div>
          <h1 className="mt-6 text-4xl sm:text-6xl font-bold tracking-[-.055em] leading-[1.03] text-white">
            Read the water.<br /><span className="text-emerald-300">Catch your window.</span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-sm sm:text-base leading-7 text-slate-400">
            Choose the level of detail that fits your sessions. Adjust your plan any time from Account.
          </p>
        </header>

        {notice && <div role={notice.tone === "error" ? "alert" : "status"} className={`mx-auto mb-5 max-w-4xl rounded-xl border px-4 py-3 text-sm ${notice.tone === "error" ? "border-rose-300/25 bg-rose-300/[.07] text-rose-100" : "border-emerald-300/20 bg-emerald-300/[.06] text-emerald-100"}`}>
          <div className="flex items-start justify-between gap-3"><p>{notice.text}</p><button className="shrink-0 text-xs underline" onClick={() => setNotice(null)}>Dismiss</button></div>
        </div>}
        {catalog.isError && <div role="alert" className="mx-auto mb-5 max-w-4xl rounded-xl border border-rose-300/25 bg-rose-300/[.07] p-4 text-sm text-rose-100">
          Monthly prices could not be loaded. No checkout will be started. <button className="ml-2 underline" onClick={() => void catalog.refetch()}>Try again</button>
        </div>}
        {catalog.data && !available && <div role="status" className="mx-auto mb-5 max-w-4xl rounded-xl border border-amber-200/20 bg-amber-200/[.06] p-4 text-sm text-amber-100">{catalog.data.message || "Paid checkout is temporarily unavailable."}</div>}
        {paidAccount && <div className="mx-auto mb-5 max-w-4xl rounded-xl border border-emerald-300/20 bg-emerald-300/[.06] px-4 py-3 text-sm text-emerald-100">
          You already have paid access. <Link href="/account" className="font-semibold underline underline-offset-4">Manage or change your plan in Account</Link>.
        </div>}

        <section aria-label="LiveSwell plans" className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
          <Plan title="Free" price="$0" summary="A clear read before you paddle out." features={features.free} kind="free">
            <Link href={isAuthenticated ? "/" : "/sign-up"} className="plan-button plan-secondary">Start exploring <ChevronRight size={16} /></Link>
          </Plan>
          <Plan title="Pro" price={catalog.isLoading ? "—" : offers?.pro.price ?? "—"} summary="Know when your spot is turning on." features={features.pro} kind="pro" highlight>
            <button className="plan-button plan-primary" disabled={nativeIos || authLoading || !available || checkout.isPending || checkoutBusy || !!paidAccount || !safeToBuy}
              onClick={() => start("pro")}>
              {nativeIos ? "Purchases unavailable in iOS app" : paidAccount ? "Manage plan in Account" : !isAuthenticated ? "Start 7-day free trial" : account.isLoading ? "Checking account…" : !safeToBuy ? "Verify billing in Account" : checkout.isPending || checkoutBusy ? "Preparing secure checkout…" : "Start 7-day free trial"}
              {!nativeIos && <ChevronRight size={16} />}
            </button>
          </Plan>
          <Plan title="Agent" price={catalog.isLoading ? "—" : offers?.agent.price ?? "—"} summary="Ask better questions of real conditions." features={features.agent} kind="agent">
            <button className="plan-button plan-primary" disabled={nativeIos || authLoading || !available || checkout.isPending || checkoutBusy || !!paidAccount || !safeToBuy}
              onClick={() => start("agent")}>
              {nativeIos ? "Purchases unavailable in iOS app" : paidAccount ? "Manage plan in Account" : !isAuthenticated ? "Start 7-day free trial" : account.isLoading ? "Checking account…" : !safeToBuy ? "Verify billing in Account" : checkout.isPending || checkoutBusy ? "Preparing secure checkout…" : "Start 7-day free trial"}
              {!nativeIos && <ChevronRight size={16} />}
            </button>
          </Plan>
        </section>
        <p className="mx-auto mt-6 max-w-4xl text-center text-[11px] leading-5 text-slate-500">
          Both paid plans include an eligible 7-day trial for new subscriptions. After the trial, the selected plan renews monthly at the price shown, plus applicable tax, until canceled. Trial eligibility is determined at checkout; repeat trials are not promised. Paddle securely handles payment.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-2"><LockKeyhole size={13} /> Secure Paddle checkout</span>
          <span className="hidden sm:inline">·</span>
          <Link href="/account" className="text-emerald-200 hover:text-emerald-100">Billing changes live in Account <ChevronRight className="inline" size={13} /></Link>
        </div>
      </main>
      <Footer />
      <style>{`
        .plan-card{position:relative;display:flex;flex-direction:column;min-width:0;border:1px solid rgba(148,163,184,.16);border-radius:18px;background:linear-gradient(155deg,rgba(22,43,55,.82),rgba(7,21,32,.94));padding:24px}
        .plan-card.pro{border-color:rgba(110,231,183,.38);background:linear-gradient(155deg,rgba(13,71,61,.52),rgba(7,21,32,.97));box-shadow:0 22px 60px rgba(0,0,0,.2)}
        .plan-card.agent{background:linear-gradient(155deg,rgba(30,61,65,.58),rgba(7,21,32,.97))}
        .plan-overline{font:700 10px ui-monospace,monospace;letter-spacing:.16em;color:#91a8aa;text-transform:uppercase}
        .plan-card h2{margin:10px 0 0;font-size:27px;letter-spacing:-.045em;color:#f4f8f5}
        .plan-summary{min-height:44px;margin:6px 0 19px;color:#9aadaf;font-size:13px;line-height:1.55}
        .plan-price{display:flex;align-items:baseline;gap:5px;margin-bottom:6px}
        .plan-price strong{font-size:44px;letter-spacing:-.065em;color:#fff}
        .plan-price span{font-size:13px;color:#9aadaf}
        .plan-terms{margin:0 0 18px;color:#81979a;font-size:11px}
        .plan-button{min-height:44px;width:100%;display:flex;align-items:center;justify-content:center;gap:8px;border-radius:9px;text-decoration:none;font-size:12px;font-weight:700;transition:transform .18s,opacity .18s}
        .plan-button:hover:not(:disabled){transform:translateY(-2px)}
        .plan-button:disabled{cursor:not-allowed;opacity:.52}
        .plan-primary{border:0;background:#78dfb5;color:#06251f;cursor:pointer}
        .plan-secondary{border:1px solid rgba(148,163,184,.2);background:rgba(148,163,184,.07);color:#e2ece8}
        .plan-benefits{display:grid;gap:13px;border-top:1px solid rgba(148,163,184,.13);margin-top:23px;padding-top:20px}
        .plan-benefit{display:flex;gap:10px;align-items:flex-start;color:#c4d0cd;font-size:12px;line-height:1.5}
        .plan-benefit svg{flex:none;color:#78dfb5;margin-top:1px}
        @media(max-width:767px){.plan-card{padding:21px}.plan-summary{min-height:0}}
      `}</style>
    </div>
  );
}

function Plan({ title, price, summary, features: items, kind, highlight = false, children }: { title: string; price: string; summary: string; features: string[]; kind: string; highlight?: boolean; children: React.ReactNode }) {
  return <article className={`plan-card ${kind}${highlight ? " pro" : ""}`}>
    <span className="plan-overline">{kind === "pro" ? "For regular sessions" : kind === "agent" ? "For the curious" : "Always free"}</span>
    <h2>{title}</h2><p className="plan-summary">{summary}</p>
    <div className="plan-price"><strong>{price}</strong>{kind !== "free" && <span>/ month</span>}</div>
    <p className="plan-terms">{kind === "free" ? "No payment details required" : "7-day trial · then monthly"} </p>
    {children}
    <div className="plan-benefits">{items.map(item => <div key={item} className="plan-benefit"><Check size={15} /><span>{item}</span></div>)}</div>
  </article>;
}
